import { APIPageQueryResult, useAPIPageQuery } from 'shared/api/APIPageQuery';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { APIQueryResult, useAPIQuery } from 'shared/api/APIQuery';
import {
  OnboardingPacketDetailsDTO,
  onboardingPacketDetailsSchema,
  OnboardingPacketDTO,
  onboardingPacketSchema,
  OnboardingPacketShipperDTO,
  onboardingPacketShipperSchema,
  OnboardingPacketWithShipperDTO,
  PacketSubmissionDTO,
  PacketSubmissionPayloadDTO,
  packetSubmissionSchema,
} from './OnboardingPacketDTO';
import { APIPageResponse, APIResponse } from 'shared/api/CarrierAPIClient';
import {
  mapFraudMonitorAPIPageToCarrierAPIPage,
  requestFraudMonitorAPI,
} from 'shared/api/FraudMonitorAPIClient';
import {
  LoadboardAPIPageResponse,
  mapLoadboardAPIPageToCarrierAPIPage,
  requestJSONLoadboardAPI,
} from 'shared/api/LoadboardAPIClient';
import { OnboardingPacketStatus } from 'shared/modules/onboarding-packets/data/OnboardingPacketDTO';

export const ONBOARDING_PACKETS_PAGE_SIZE = 20;

interface OnboardingPacketsParams {
  status?: OnboardingPacketStatus;
  page: number;
  limit: number;
}

export function useOnboardingPackets({
  status,
  page,
  limit,
}: OnboardingPacketsParams): APIPageQueryResult<OnboardingPacketDTO> {
  return useAPIPageQuery(
    ['onboarding-packets', 'list', { status, page, limit }],
    () =>
      requestFraudMonitorAPI<APIPageResponse<OnboardingPacketDTO>>([
        'GET /api/v1/carrier-onboarding/carrier/packets{?status,page,limit}',
        { status, page, limit },
      ]).then(mapFraudMonitorAPIPageToCarrierAPIPage),
    { schema: onboardingPacketSchema, refetchOnWindowFocus: false },
  );
}

export function useOnboardingPacketShippers(
  guids: string[],
): APIPageQueryResult<OnboardingPacketShipperDTO> {
  const uniqueGuids = useMemo(() => [...new Set(guids)].sort(), [guids]);

  return useAPIPageQuery(
    ['onboarding-packets', 'shippers', { guids: uniqueGuids }],
    () =>
      requestJSONLoadboardAPI<
        LoadboardAPIPageResponse<OnboardingPacketShipperDTO>
      >([
        'GET /internal/v3/shippers/by-guids{?guids*}',
        { guids: uniqueGuids },
      ]).then(mapLoadboardAPIPageToCarrierAPIPage),
    {
      enabled: uniqueGuids.length > 0,
      schema: onboardingPacketShipperSchema,
      refetchOnWindowFocus: false,
    },
  );
}

export function useOnboardingPacketShipper(
  guid: string | undefined,
): OnboardingPacketShipperDTO | undefined {
  const guids = useMemo(() => (guid ? [guid] : []), [guid]);
  const { data: shippers } = useOnboardingPacketShippers(guids);

  return shippers?.data.find((shipper) => shipper.guid === guid);
}

export function useOnboardingPacketsWithShippers(
  params: OnboardingPacketsParams,
): { data: APIPageResponse<OnboardingPacketWithShipperDTO> | undefined } {
  const { data: packets } = useOnboardingPackets(params);
  const shipperGuids = useMemo(
    () => packets?.data.map((packet) => packet.shipper_guid) ?? [],
    [packets],
  );
  const { data: shippers } = useOnboardingPacketShippers(shipperGuids);

  const data = useMemo(() => {
    if (!packets) return undefined;

    const shippersByGuid = new Map(
      shippers?.data.map((shipper) => [shipper.guid, shipper]),
    );

    return {
      ...packets,
      data: packets.data.map((packet) => ({
        ...packet,
        shipper: shippersByGuid.get(packet.shipper_guid),
      })),
    };
  }, [packets, shippers]);

  return { data };
}

export function useOnboardingPacket(
  guid: string | undefined,
): APIQueryResult<OnboardingPacketDetailsDTO> {
  return useAPIQuery(
    ['onboarding-packets', 'details', { guid }],
    () =>
      requestFraudMonitorAPI<APIResponse<OnboardingPacketDetailsDTO>>([
        'GET /api/v1/carrier-onboarding/carrier/packets/{guid}',
        { guid },
      ]),
    {
      enabled: !!guid,
      schema: onboardingPacketDetailsSchema,
      refetchOnWindowFocus: false,
    },
  );
}

export function useOnboardingPacketAPI() {
  const queryClient = useQueryClient();

  return useMemo(
    () => ({
      submitPacket(
        guid: string,
        payload: PacketSubmissionPayloadDTO,
      ): Promise<APIResponse<PacketSubmissionDTO>> {
        return requestFraudMonitorAPI<APIResponse<PacketSubmissionDTO>>(
          [
            'PUT /api/v1/carrier-onboarding/carrier/packets/{guid}/submission',
            { guid },
          ],
          { json: payload },
        ).then((response) => {
          void queryClient.invalidateQueries({
            queryKey: ['onboarding-packets'],
          });

          return {
            ...response,
            data: packetSubmissionSchema.parse(response.data),
          };
        });
      },
    }),
    [queryClient],
  );
}
